import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Claim {
  claim_id: string;
  fh_claim_num: string;
  tier_claim_num: string;
  open_close_status: string;
  loss_date: string;
  handler_name: string;
  company_name: string;
  claimant_first_name: string;
  claimant_last_name: string;
  total_incurred: number;
  total_outstanding: number;
  total_paid: number;
  coverage_type: string;
  insured_name: string;
}

interface ClaimsResponse {
  claims: Claim[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
}

function StatCard({ label, value, sub }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-5 flex flex-col gap-1 min-w-0">
      <p className="text-sm font-medium text-slate-500 truncate">{label}</p>
      <p className="text-3xl font-bold text-blue-700 tabular-nums truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400 truncate">{sub}</p>}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function Dashboard() {
  const navigate = useNavigate();

  // Filter / pagination state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('O');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Data state
  const [data, setData] = useState<ClaimsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce search input — 300 ms
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 300);
  };

  // Reset to page 1 when filters change
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  // Fetch claims
  const fetchClaims = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(pageSize),
        sort_by: 'loss_date',
        order: 'desc',
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;

      const result: ClaimsResponse = await api.getClaims(params);
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load claims.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  // Derived stats
  const claims = data?.claims ?? [];
  const totalClaims = data?.total ?? 0;
  const openCount = claims.filter((c) => c.open_close_status === 'O').length;
  const totalIncurred = claims.reduce((sum, c) => sum + (c.total_incurred ?? 0), 0);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Claims Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">FileHandler Claims Intelligence</p>
        </div>

        {/* ── Summary Stats ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Total Claims"
            value={totalClaims.toLocaleString()}
            sub="across all statuses"
          />
          <StatCard
            label="Open Claims"
            value={openCount.toLocaleString()}
            sub="on current page"
          />
          <StatCard
            label="Total Incurred"
            value={formatCurrency(totalIncurred)}
            sub="on current page"
          />
        </div>

        {/* ── Search / Filter Bar ────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Text search */}
            <div className="relative flex-1">
              <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-4.35-4.35M16.65 16.65A7.5 7.5 0 1110.5 3a7.5 7.5 0 016.15 13.65z" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search claim # or claimant name…"
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50 placeholder-slate-400"
              />
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="sm:w-44 py-2 px-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-700"
            >
              <option value="">All Statuses</option>
              <option value="O">Open</option>
              <option value="C">Closed</option>
              <option value="R">Reopened</option>
            </select>
            {/* Sort label — always loss date desc */}
            <span className="hidden sm:flex items-center text-xs text-slate-400 whitespace-nowrap">
              Sorted: Most Recent First
            </span>
          </div>
        </div>

        {/* ── Main Content ───────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-6 py-10 flex flex-col items-center gap-4">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-slate-700 font-medium">{error}</p>
            <button
              onClick={fetchClaims}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* ── Claims Table ─────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">TCM #</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Claimant</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Insured</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Handler</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Loss Date</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Total Incurred</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                          No claims found.
                        </td>
                      </tr>
                    ) : (
                      claims.map((claim, idx) => (
                        <tr
                          key={claim.claim_id}
                          onClick={() => navigate(`/claims/${claim.claim_id}`)}
                          className={[
                            'border-b border-slate-100 cursor-pointer transition-colors',
                            idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60',
                            'hover:bg-blue-50',
                          ].join(' ')}
                        >
                          <td className="px-4 py-3 font-mono text-blue-700 whitespace-nowrap font-medium">
                            {claim.tier_claim_num || claim.fh_claim_num || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-800 whitespace-nowrap">
                            {[claim.claimant_first_name, claim.claimant_last_name]
                              .filter(Boolean)
                              .join(' ') || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap max-w-[180px] truncate">
                            {claim.insured_name || '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={claim.open_close_status} />
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {claim.handler_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {formatDate(claim.loss_date)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-800 tabular-nums whitespace-nowrap font-medium">
                            {formatCurrency(claim.total_incurred)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Pagination Bar ───────────────────────────────────────── */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-600">
              {/* Left: total info */}
              <p className="text-slate-500">
                {totalClaims > 0
                  ? `Showing ${((page - 1) * pageSize) + 1}–${Math.min(page * pageSize, totalClaims)} of ${totalClaims.toLocaleString()} claims`
                  : 'No claims'}
              </p>

              {/* Center: prev / page / next */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 font-medium">
                  Page {page} of {data?.pages ?? 1}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(data?.pages ?? 1, p + 1))}
                  disabled={page >= (data?.pages ?? 1)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Next
                </button>
              </div>

              {/* Right: page size selector */}
              <div className="flex items-center gap-2">
                <label className="text-slate-500 whitespace-nowrap">Rows per page:</label>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="py-1.5 px-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-700"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
